import { Button, Typography } from "@material-ui/core";
import { AccessTime, Close, ArrowBack, ArrowForward } from "@material-ui/icons";
import { DateTime } from "luxon";
import { useContext, useEffect, useState, useCallback } from "react";

import { useStyles } from "@/common/components/ArchivePage/ImageCarousel/ImageCarouselStyles";
import { LiveUnitMetadataContext } from "@/common/contexts/LiveUnitMetadataContext";
import { ArchiveImage, ArchiveImagesNavMeta } from "@/utilities/types/archive";


interface ImageCarouselProps {
    cameraName: string;
    presetName: string;
    presetImages: ArchiveImage[];
    selectedImageIndex: number;
    setIsOpen: (bool: boolean) => void;
    imageCarouselOpen: boolean;
}

const ImageCarousel: React.FC<ImageCarouselProps> = (props: ImageCarouselProps) => {
    const { cameraName, presetName, presetImages, selectedImageIndex, setIsOpen, imageCarouselOpen } = props;
    const [currentIndex, setCurrentIndex] = useState<number>(selectedImageIndex);
    const [thumbnailsOffset, setThumbnailsOffset] = useState<number>(0);

    const unitMetadata = useContext<ArchiveImagesNavMeta>(LiveUnitMetadataContext);
    const classes = useStyles();

    useEffect(() => {
        setCurrentIndex(selectedImageIndex);
    }, [selectedImageIndex]);

    useEffect(() => {
        setThumbnailsOffset(currentIndex * 144);
    }, [currentIndex]);

    const handleGoingLeft = useCallback(
        (idx: number) => {
            const leftVal = idx - 1;

            return leftVal > -1 ? leftVal : idx;
        },
        [presetImages],
    );

    const handleGoingRight = useCallback(
        (idx: number) => {
            const rightVal = idx + 1;

            return rightVal < presetImages.length ? rightVal : idx;
        },
        [presetImages],
    );

    useEffect(() => {
        if (!imageCarouselOpen) return;

        const keyListener = (e: any) => {
            switch (e.key) {
                //Escape Key
                case "Escape":
                    setIsOpen(false);
                    break;
                //Left Key
                case "ArrowLeft":
                    setCurrentIndex((prevState) => handleGoingLeft(prevState));
                    break;
                //Right Key
                case "ArrowRight":
                    setCurrentIndex((prevState) => handleGoingRight(prevState));
                    break;
            }
        };

        window.addEventListener("keydown", keyListener);

        return () => {
            window.removeEventListener("keydown", keyListener);
        };
    }, [handleGoingLeft, handleGoingRight]);

    // const handleViewOnTimeline = () => {
    //     setIsOpen(false);

    //     const currentImageWithPadding = DateTime.fromISO(presetImages[currentIndex].datetimeRecorded)
    //         .minus({ minutes: 20 })
    //         .toISO();

    //     dispatch(setTimelineStart(currentImageWithPadding));
    // };

    // const downloadImage = async (url: string) => {
    //     const img = await fetchDownloadableImage(url);
    //     const el = document.createElement("a");

    //     el.href = URL.createObjectURL(img);
    //     el.download = `${cameraName} - ${presetName} - ${DateTime.fromISO(presetImages[currentIndex]?.datetimeRecorded)
    //         .setZone(unitMetadata.timezone)
    //         .toFormat("MMM_dd_y hh_mm_ss a")}.jpeg`;
    //     document.body.appendChild(el);
    //     el.click();
    //     document.body.removeChild(el);
    // };

    // Reason for toFormat("HH:mm") in line 76 instead of .toLocaleString(DateTime.TIME_24_SIMPLE)
    // https://github.com/moment/luxon/issues/726
    // https://github.com/moment/luxon/issues/726#issuecomment-682834834
    return (
        <div className={classes.carousel}>
            <div className={classes.carouselHeader}>
                <div className={classes.carouselHeaderSection}>
                    <AccessTime className={classes.icon} />
                    <Typography className={classes.dateTime}>
                        {`${DateTime.fromISO(presetImages[currentIndex]?.datetimeRecorded)
                            .setZone(unitMetadata.timezone)
                            .toFormat("HH:mm")}
                            \u2022
                            ${DateTime.fromISO(presetImages[currentIndex]?.datetimeRecorded)
                                .setZone(unitMetadata.timezone)
                                .toLocaleString(DateTime.DATE_FULL)}`}
                    </Typography>
                    <Typography>
                        {cameraName} - {presetName}
                    </Typography>
                </div>
                <div className={classes.carouselHeaderSection}>
                    {/* <FeatureButton color="default" onClick={() => handleViewOnTimeline()} variant="contained">
                        <Search className={classes.icon} />
                        <Typography variant="inherit">View on Timeline</Typography>
                    </FeatureButton> */}
                    {/* <FeatureButton
                        color="default"
                        onClick={() => downloadImage(presetImages[currentIndex].imageUrl)}
                        variant="contained"
                    >
                        <GetApp className={classes.icon} />
                        <Typography variant="inherit">Download Image</Typography>
                    </FeatureButton> */}
                    <Button onClick={() => setIsOpen(false)} className={classes.close} variant="text">
                        <Close />
                    </Button>
                </div>
            </div>
            <div className={classes.viewer}>
                <Button className={classes.arrow} onClick={() => setCurrentIndex((prevState) => handleGoingLeft(prevState))}>
                    <ArrowBack />
                </Button>
                <img src={presetImages[currentIndex]?.imageUrl} alt="" className={classes.fullImage} />
                <Button className={classes.arrow} onClick={() => setCurrentIndex((prevState) => handleGoingRight(prevState))}>
                    <ArrowForward />
                </Button>
            </div>
            <div
                className={classes.thumbnailTrack}
                style={{ transform: `translateX(calc(50% - 72px - ${thumbnailsOffset}px))` }}
            >
                {presetImages.map((img, i) => {
                    const current = currentIndex === i ? classes.highlighted : undefined;

                    return (
                        img.thumbUrl && (
                            <div key={i} className={classes.thumbnail}>
                                <img src={img.thumbUrl} alt="" className={current} onClick={() => setCurrentIndex(i)} />
                            </div>
                        )
                    );
                })}
            </div>
        </div>
    );
};

export default ImageCarousel;
